import { DEG } from './warmupShape';

/**
 * The warmup synth — ported from the prototype
 * (`docs/refs/warmups/cyv-warmup-catalog-v2-review.html`), deliberately note
 * for note: a two-oscillator voice (triangle + a quarter-gain sine an octave
 * up), glides as linear frequency ramps, staccato shortening, an unpitched
 * noise tick, a three-note key cue, and the echo call on rests. Tonic C4
 * (MIDI 60), one pass. Anthony has heard this and approved the sound, so the
 * envelope numbers are a spec rather than a starting point — change them only
 * on purpose.
 *
 * What is NOT ported is the prototype's DOM handling: it mutated button text
 * directly and tracked one global `playing` reference. Here `playPattern`
 * returns the pattern's duration in ms and a `stop()`, and React owns the
 * button label — same behaviour, no DOM writes from an audio module.
 *
 * iOS. Safari starts every AudioContext `suspended` and will only resume it
 * inside a user gesture. The Play tap IS that gesture, so `resume()` must be
 * called synchronously on the tap — before any `await` — or the resume is no
 * longer attributed to the gesture and the first play is silent with no error.
 * `ensureContext` is therefore synchronous, and `playPattern` never awaits
 * before scheduling.
 */

const TONIC_MIDI = 60; // C4
const A4 = 440;

let ctx = null;

/** Hz for a MIDI note number. */
const midiToHz = (midi) => A4 * Math.pow(2, (midi - 69) / 12);

/**
 * Get (or lazily create) the shared AudioContext and resume it if suspended.
 * SYNCHRONOUS on purpose — see the iOS note above.
 * Returns null where Web Audio does not exist at all (jsdom, old browsers), so
 * every caller degrades to "no sound" rather than throwing.
 */
export const ensureContext = () => {
	const Ctor = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
	if (!Ctor) return null;
	if (!ctx) {
		try {
			ctx = new Ctor();
		} catch {
			return null;
		}
	}
	// A promise, but never awaited: resuming must be *requested* inside the
	// gesture, and the scheduling below is safe either way.
	if (ctx.state === 'suspended' && typeof ctx.resume === 'function') ctx.resume();
	return ctx;
};

/** One sung note. `glideTo` ramps the pitch across the note's whole length. */
const tone = (a, freq, t0, dur, glideTo) => {
	const o = a.createOscillator();
	const o2 = a.createOscillator();
	const g = a.createGain();
	o.type = 'triangle';
	o2.type = 'sine';
	o.frequency.setValueAtTime(freq, t0);
	o2.frequency.setValueAtTime(freq * 2, t0);
	if (glideTo) {
		o.frequency.linearRampToValueAtTime(glideTo, t0 + dur);
		o2.frequency.linearRampToValueAtTime(glideTo * 2, t0 + dur);
	}
	g.gain.setValueAtTime(0, t0);
	g.gain.linearRampToValueAtTime(0.28, t0 + 0.015);
	g.gain.exponentialRampToValueAtTime(0.12, t0 + Math.min(0.25, dur * 0.5));
	g.gain.setTargetAtTime(0.0001, t0 + dur - 0.03, 0.02);
	const g2 = a.createGain();
	g2.gain.value = 0.25;
	o2.connect(g2).connect(g);
	o.connect(g).connect(a.destination);
	o.start(t0);
	o2.start(t0);
	o.stop(t0 + dur + 0.05);
	o2.stop(t0 + dur + 0.05);
	return [o, o2];
};

/** An unpitched click — the "X" step, used for counting and rhythm drills. */
const tick = (a, t0) => {
	const b = a.createBuffer(1, a.sampleRate * 0.04, a.sampleRate);
	const d = b.getChannelData(0);
	for (let i = 0; i < d.length; i += 1) {
		d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (a.sampleRate * 0.006));
	}
	const s = a.createBufferSource();
	s.buffer = b;
	const g = a.createGain();
	g.gain.value = 0.35;
	s.connect(g).connect(a.destination);
	s.start(t0);
	return [s];
};

/** The key cue: a C-major triad before the pattern, so the ear has a tonic. */
const chord = (a, t0, dur) => [48, 52, 55].flatMap((m) => tone(a, midiToHz(m), t0, dur));

/**
 * Schedule one pass of an exercise.
 *
 * @returns {{durationMs:number, stop:() => void}|null} null when there is no
 *   audio support at all, so the caller can leave the button alone.
 */
export const playPattern = (exercise) => {
	const a = ensureContext();
	if (!a || !exercise || !Array.isArray(exercise.pattern)) return null;

	const beat = 60 / (exercise.bpm || 80);
	let t = a.currentTime + 0.05;
	const nodes = [];
	const glides = new Set((exercise.glides || []).map((g) => `${g[0]}-${g[1]}`));

	nodes.push(...chord(a, t, beat * 2)); // key cue
	t += beat * 2;

	exercise.pattern.forEach((n, i) => {
		const dur = n[1] * beat;
		if (n[0] === 'R') {
			// A rest — unless this is a call-and-response exercise, in which case
			// the first three rests are the "call" the student answers.
			if (exercise.echo && i < 3) {
				const call = ['5', '3', '1'][i];
				nodes.push(...tone(a, midiToHz(TONIC_MIDI + DEG[call]), t, dur * 0.95));
			}
			t += dur;
			return;
		}
		if (n[0] === 'X') {
			for (let k = 0; k < n[1]; k += 1) nodes.push(...tick(a, t + k * beat));
			t += dur;
			return;
		}
		const f = midiToHz(TONIC_MIDI + DEG[n[0]]);
		const nx = exercise.pattern[i + 1];
		let to = null;
		if (nx && glides.has(`${i}-${i + 1}`) && DEG[nx[0]] !== undefined) {
			to = midiToHz(TONIC_MIDI + DEG[nx[0]]);
		}
		const len = exercise.artic === 'staccato' ? Math.min(dur * 0.45, 0.18) : dur * 0.95;
		nodes.push(...tone(a, f, t, len, to));
		t += dur;
	});

	const durationMs = (t - a.currentTime) * 1000;
	const stop = () => {
		nodes.forEach((n) => {
			try {
				n.stop();
			} catch {
				/* already stopped, or not stoppable */
			}
		});
	};
	return { durationMs, stop };
};

/** Test seam — drops the cached context so a suite can assert on a fresh one. */
export const _resetAudioForTests = () => {
	ctx = null;
};
